import React, { useState, useRef, useEffect } from "react";
import { FaEnvelope, FaReceipt, FaSearch, FaSpinner } from "react-icons/fa";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar } from "lucide-react";
import CommonContainer from "../Container/CommonContainer";
import toast from "react-hot-toast";
import { formatDate2 } from "../../Common/CommonVariables";
import * as ReportServices from "../../Services/Reports/ReportServices";
import LoadingSpinnerComponent from "../Loader/LoadingSpinnerComponent";
import { store } from "../../Store/store";
import { LoadingMessage } from "../../Common/CommonVariables";
import "./InvoiceReportComponent.css";
import { currencySymbols } from '../../Common/CommonVariables';
import { useInvoiceTotalOutstanding, useInvoicePayNow, useDownloadInvoiceReport } from "../../Common/CommonAccessFile";

const InvoiceReportComponent = () => {
  const userInfo = store.getState();
  const userData = userInfo?.userData || {};

  const accessToBulkPatientReportDownload = useDownloadInvoiceReport()
  const accessToInvoicePayNow = useInvoicePayNow()
  const accessToInvoiceTotalOutstanding = useInvoiceTotalOutstanding()

  const today = new Date();
  // const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  // const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  // Get the date 90 days ago from today
  const date90DaysAgo = new Date();
  date90DaysAgo.setDate(today.getDate() - 90);

  // Get the first day of that month
  const firstDayOf90DayMonth = new Date(date90DaysAgo.getFullYear(), date90DaysAgo.getMonth(), 1);

  // Today's date as the last day
  const lastDay = today;

  const [startDate, setStartDate] = useState(firstDayOf90DayMonth);
  const [endDate, setEndDate] = useState(lastDay);
  const [dateRangeOption, setDateRangeOption] = useState("Last 3 Months");

  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [summary, setSummary] = useState(null);

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const handleDateRangeChange = (e) => {
    const selected = e.target.value;
    setDateRangeOption(selected);

    const today = new Date();
    let start = null;
    let end = new Date();

    switch (selected) {
      case "Last Month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "Last 2 Months":
        start = new Date();
        start.setDate(today.getDate() - 60);
        break;
      case "Last 3 Months":
        start = new Date();
        start.setDate(today.getDate() - 90);
        break;
      case "Custom":
        start = null;
        end = null;
        break;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const fetchInvoices = () => {
    setInvoiceData([]);
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    const diff = (endDate - startDate) / (1000 * 60 * 60 * 24);

    if (startDate > endDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    if (dateRangeOption === "Custom" && diff > 200) {
      toast.error("Custom range cannot exceed 200 days.");
      return;
    }

    setLoading(true);

    const requestData = {
      startDate: formatDate2(startDate),
      endDate: formatDate2(endDate),
      clientCode: userData.Partner_Code,
      dateRangeOption: dateRangeOption
    };

    ReportServices.GetInvoiceReport_date(requestData)
      .then(({ data }) => {
        //debugger;
        if (data.IsSuccess) {
          setInvoiceData(data.Data);
        } else {
          setInvoiceData(null);
        }
      })
      .catch(() => {
        toast.error("Something went wrong.");
        setInvoiceData([]);
      })
      .finally(() => setLoading(false));
  };

  const parseDate = (dateStr) => {
    const [day, month, year] = dateStr.split("/");
    return new Date(`${year}-${month}-${day}`);
  };

  useEffect(() => {

    if (dateRangeOption) {
      fetchInvoices();
    }
  }, [dateRangeOption]);

  const handleResendInvoice = (invoiceNumber) => {
    setLoading(true);

    const payload = {
      clientCode: userData.Partner_Code,
      invoiceNumber: invoiceNumber,
      EmailReq: "Yes"
    }

    ReportServices.ResendInvoice(payload)
      .then(({ data }) => {
        //debugger;
        if (data.IsSuccess) {
          toast.success("Invoice Resend Successfully");
        } else {
          toast.error(data.Data.Reasons);
        }
      })
      .catch(() => {
        toast.error("Something went wrong.");
      })
      .finally(() => setLoading(false));
  }

  return (
    <>
      {/* {loading && <LoadingSpinnerComponent />} */}
      <CommonContainer icon={<FaReceipt size={20} />} heading="Invoice Report">
        <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-2 mb-2 invoice-report-top">
          <div className="d-flex flex-column flex-md-row align-items-md-center">
            <div className="d-flex flex-column flex-md-row gap-2">
              {/* Date Range Dropdown */}
              <select
                className="form-select custom-select"
                value={dateRangeOption}
                onChange={handleDateRangeChange}
                style={{}}
              >
                <option>Last Month</option>
                <option>Last 2 Months</option>
                <option>Last 3 Months</option>
                <option>Custom</option>
              </select>

              {/* Custom Date Pickers */}
              {dateRangeOption === "Custom" && (
                <>
                  <div className="position-relative">
                    <ReactDatePicker
                      selected={startDate}
                      onChange={setStartDate}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Start Date"
                      ref={startDateRef}
                      maxDate={new Date()}
                    />
                    <Calendar
                      size={20}
                      className="position-absolute"
                      style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                      onClick={() => startDateRef.current.setFocus()}
                    />
                  </div>

                  <div className="position-relative">
                    <ReactDatePicker
                      selected={endDate}
                      onChange={setEndDate}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      placeholderText="End Date"
                      ref={endDateRef}
                      maxDate={new Date()}
                    />
                    <Calendar
                      size={20}
                      className="position-absolute"
                      style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                      onClick={() => endDateRef.current.setFocus()}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mt-2 mt-md-0 ml-md-2">
              {/* Search Button */}
              <button
                className="btn custom-sumbitbtn d-flex align-items-center justify-content-center p-1 invoice-report-search-btn"
                style={{ height: "35px", padding: "0" }}
                onClick={fetchInvoices}
                disabled={loading}
              >
                {loading ? <FaSpinner className="fa-spin" /> : <b>GO</b>}
              </button>
            </div>
          </div>

          {loading && <LoadingMessage />}

          {(invoiceData?.ClientOutstandingAmt && invoiceData?.ClientGLBalance) && (
            <div className="invoice-data-wrapper">
              {/* <small className="d-block">
                <span className="text-danger fw-bold">Note:</span> Payment may reflect after 30 mins
              </small> */}

              <div className="bg-light p-1 rounded text-start" style={{}}>
                <div className="fw-semibold text-dark">
                  Current Period (
                  {dateRangeOption === "Custom"
                    ? `${startDate ? startDate.toLocaleDateString("en-GB") : "Start"} - ${endDate ? endDate.toLocaleDateString("en-GB") : "End"}`
                    : dateRangeOption}
                  )
                  : <span className="text-success">{currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{invoiceData.ClientOutstandingAmt}</span>
                </div>
                {accessToInvoiceTotalOutstanding && (
                  <div className="fw-semibold text-dark">
                    Total Outstanding: <span className="text-danger">{currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{invoiceData.ClientGLBalance}</span>
                  </div>
                )}
                <div style={{ fontSize: "9px" }}> <span className="text-danger fw-bold">*</span>Freepay payments may take up to 30 minutes to reflect.</div>
              </div>
            </div>
          )}
        </div>

        {/* Invoice Table */}
        <div className="table-responsive">
          <table className="table table-bordered custom-table">
            <thead className="table-light">
              <tr>
                <th style={{ width: '5%' }}>#</th>
                <th style={{ width: '7%' }}>Invoice No</th>
                <th style={{ width: '10%' }}>Date</th>
                <th style={{ width: '10%' }}>Due Date</th>
                <th style={{ width: '10%' }}>Status</th>
                <th style={{ width: '10%' }}>Inv Amount</th>
                <th style={{ width: '10%' }}>Outstanding</th>
                {accessToInvoicePayNow || accessToBulkPatientReportDownload || userData.Role_Name === "Sales-Admin" ? (
                  <th style={{ width: '38%' }}>Action</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {invoiceData?.invDetails?.length > 0 ? (
                invoiceData.invDetails.map((inv, i) => (
                  <tr key={i} style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <td style={{ width: "5%", textAlign: "center", verticalAlign: "middle" }}>{i + 1}</td>
                    <td style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>{inv.invoiceNumber}</td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>{inv.invoiceDate}</td>
                    {/* <td style={{ textAlign: "center", verticalAlign: "middle" }}>{inv.invoiceDueDate}</td> */}
                    <td
                      style={{ textAlign: "center", verticalAlign: "middle" }}
                      className={new Date() > parseDate(inv.invoiceDueDate) ? "text-danger" : ""}
                    >
                      {inv.invoiceDueDate}
                    </td>
                    <td style={{ width: "8%", textAlign: "center", verticalAlign: "middle" }}>
                      <span
                        className={`badge-inv ${inv.invoiceStatus === "Open" ? "bg-warning" : "bg-success"
                          }`}
                      >
                        {inv.invoiceStatus}
                      </span>
                    </td>
                    <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                      {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{parseFloat(inv.invoiceAmount).toLocaleString()}&nbsp;
                    </td>
                    {/* <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{parseFloat(inv.invoiceOpenAmount).toLocaleString()}
                    </td> */}
                    <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                      <div style={{
                        position: "relative", display: "inline-block",
                        paddingRight:
                          parseFloat(inv.invoiceOpenAmount) < parseFloat(inv.invoiceAmount)
                            ? "12px"
                            : "0px"
                      }}>
                        {parseFloat(inv.invoiceOpenAmount) < parseFloat(inv.invoiceAmount) && (
                          <i
                            className="fas fa-info-circle text-muted"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Partially Paid"
                            style={{
                              fontSize: "12px",
                              position: "absolute",
                              top: "-5px",
                              right: "2px",
                              cursor: "pointer"
                            }}
                          ></i>
                        )}
                        <span style={{ right: "10px" }}>
                          {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}
                          {parseFloat(inv.invoiceOpenAmount).toLocaleString()}
                        </span>
                      </div>
                    </td>

                    {/* <td style={{ textAlign: "right", verticalAlign: "middle", position: "relative" }}>
                      {parseFloat(inv.invoiceOpenAmount) < parseFloat(inv.invoiceAmount) && (
                        <i
                          className="fas fa-info-circle text-muted"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title="Partially Paid"
                          style={{
                            fontSize: "12px",
                            position: "absolute",
                            top: "3px",
                            right: "35px",
                            cursor: "pointer"
                          }}
                        ></i>
                      )}
                      {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{parseFloat(inv.invoiceOpenAmount).toLocaleString()}
                    </td> */}
                    {accessToInvoicePayNow || accessToBulkPatientReportDownload || userData.Role_Name === "Sales-Admin" ? (
                      <td style={{ width: "20%", verticalAlign: "middle" }}>
                        <div className="container">
                          <div className="row justify-content-center text-center">
                            {accessToBulkPatientReportDownload && (
                              <div className="col-4">
                                {inv.invoicepdfURL ? (
                                  <a
                                    href={inv.invoicepdfURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-warning btn-sm w-100"
                                    download
                                  >
                                    Download
                                  </a>
                                ) : "—"
                                }
                              </div>
                            )}
                            {accessToInvoicePayNow && (
                              <div className="col-4">
                                {inv.freepayQRCodeLink ? (
                                  <a
                                    href={inv.freepayQRCodeLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-success btn-sm w-100"
                                  >
                                    Pay Now
                                  </a>) : "—"}
                              </div>
                            )}
                            {userData.Role_Name === "Sales-Admin" && (
                              <div className="col-4">
                                {/* Add resend check */}
                                {/* {inv.freepayQRCodeLink ? ( */}
                                <button
                                  className="btn btn-success btn-sm w-100"
                                  onClick={() => handleResendInvoice(inv.invoiceNumber)}
                                  disabled={loading}
                                >
                                  <FaEnvelope className="mr-1"/>
                                  Resend
                                </button>
                                {/* ) : "—"} */}
                              </div>
                            )}

                          </div>
                        </div>
                      </td>
                    ) : null}


                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    No invoices found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </CommonContainer >
    </>
  );
};

export default InvoiceReportComponent;

// import React, { useState, useRef } from "react";
// import { FaSpinner, FaDownload, FaSearch } from "react-icons/fa";
// import "../../Complaint.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import CommonContainer from "../Container/CommonContainer";
// import toast from "react-hot-toast";
// import { formatDate, formatDate2 } from "../../Common/CommonVariables";
// import * as ReportServices from "../../Services/Reports/ReportServices";
// import LoadingSpinnerComponent from "../Loader/LoadingSpinnerComponent";
// import { store } from "../../Store/store";
// import ReactDatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import { Calendar } from "lucide-react";

// const InvoiceReportComponent = () => {
//   const userInfo = store.getState();
//   const userData = userInfo?.userData || {};

//   const today = new Date();
//   const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

//   const [startDate, setStartDate] = useState(firstDayOfMonth);
//   const [endDate, setEndDate] = useState(today);

//   const startDateRef = useRef(null);
//   const endDateRef = useRef(null);

//   const [loading, setLoading] = useState(false);
//   const [invoiceData, setInvoiceData] = useState(null);

//   const downloadInvoiceReport = () => {
//     if (!startDate || !endDate) {
//       toast.error("Date can't be null/empty");
//       return;
//     }
//     if (startDate > endDate) {
//       toast.error("Start date cannot be after end date");
//       return;
//     }
//     if (endDate > new Date()) {
//       toast.error("End date cannot be in the future");
//       return;
//     }
//     if (!userData.Partner_Code) {
//       toast.error("No account is mapped to get report");
//       return;
//     }

//     setLoading(true);

//     const requestData = {
//       startDate: formatDate2(startDate),
//       endDate: formatDate2(endDate),
//       clientCode: userData.Partner_Code,
//     };

//     ReportServices.GetInvoiceReport(requestData)
//       .then(({ data }) => {
//         if (data.IsSuccess) {
//           setInvoiceData(data.Data || null);
//         } else {
//           setInvoiceData(null);
//         }
//       })
//       .catch(() => {
//         toast.error("Something went wrong.");
//         setInvoiceData(null);
//       })
//       .finally(() => setLoading(false));
//   };

//   return (
//     <>
//       {loading && <LoadingSpinnerComponent />}

//       <CommonContainer heading="Invoice Report">
//         {/* Date filters */}
//         <div className="d-flex align-items-center gap-3 mb-3">
//           <div className="position-relative">
//             {/* <label>Start Date:</label>&nbsp; */}
//             <ReactDatePicker
//               selected={startDate}
//               onChange={setStartDate}
//               className="form-control"
//               dateFormat="dd-MM-yyyy"
//               placeholderText="Select Start Date"
//               ref={startDateRef}
//             />
//             <Calendar
//               size={20}
//               className="position-absolute"
//               style={{ right: "10px", top: "60%", transform: "translateY(-50%)" }}
//               onClick={() => startDateRef.current.setFocus()}
//             />
//           </div>

//           <div className="position-relative">
//             {/* <label>End Date:</label>&nbsp; */}
//             <ReactDatePicker
//               selected={endDate}
//               onChange={setEndDate}
//               className="form-control"
//               dateFormat="dd-MM-yyyy"
//               placeholderText="Select End Date"
//               ref={endDateRef}
//             />
//             <Calendar
//               size={20}
//               className="position-absolute"
//               style={{ right: "10px", top: "60%", transform: "translateY(-50%)" }}
//               onClick={() => endDateRef.current.setFocus()}
//             />
//           </div>

//           <button
//             className="btn custom-sumbitbtn d-flex align-items-center justify-content-center"
//             style={{ width: "38px", height: "35px", padding: "0" }}
//             onClick={downloadInvoiceReport}
//             disabled={loading}
//           >
//             {loading ? <FaSpinner className="fa-spin" /> : <FaSearch />}
//           </button>

//           {invoiceData && (
//             <div className="card">
//               <div className="row">
//                 <div className="col-md-12"><strong>Outstanding:</strong> {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{invoiceData.ClientOutstandingAmt}</div>
//                 <div className="col-md-12"><strong>Balance:</strong> {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{invoiceData.ClientGLBalance}</div>
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Client summary */}
//         {/* {invoiceData && (
//           <div className="card">
//             <h5 className="mb-3">Client Summary</h5>
//             <div className="row">
//               <div className="col-md-2"><strong>Outstanding:</strong> {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{invoiceData.ClientOutstandingAmt}</div>
//               <div className="col-md-2"><strong>GL Balance:</strong> {currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{invoiceData.ClientGLBalance}</div>
//             </div>
//           </div>
//         )} */}

//         {/* Invoice list */}
//         <div className="table-responsive" style={{ maxHeight: "600px" }}>
//           <table className="table table-bordered custom-table">
//             <thead className="table-light">
//               <tr>
//                 <th>#</th>
//                 <th>Invoice No</th>
//                 <th>Date</th>
//                 <th>Due Date</th>
//                 <th>Status</th>
//                 <th>Amount</th>
//                 <th>Outstanding</th>
//                 <th>Action</th>
//                 {/* <th>PDF</th> */}
//               </tr>
//             </thead>
//             <tbody>
//               {invoiceData?.invDetails?.length > 0 ? (
//                 invoiceData.invDetails.map((inv, i) => (
//                   <tr key={i}>
//                     <td>{i + 1}</td>
//                     <td>{inv.invoiceNumber}</td>
//                     <td>{inv.invoiceDate}</td>
//                     <td>{inv.invoiceDueDate}</td>
//                     <td>
//                       <span className={`badge ${inv.invoiceStatus === "Open" ? "bg-warning" : "bg-success"}`}>
//                         {inv.invoiceStatus}
//                       </span>
//                     </td>
//                     <td>{currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{parseFloat(inv.invoiceAmount).toLocaleString()}</td>
//                     <td>{currencySymbols[userInfo?.Partner_Account_Details?.CurrencyIsoCode]}{parseFloat(inv.invoiceOpenAmount).toLocaleString()}</td>
//                     <td>
//                       <a
//                         href={inv.freepayQRCodeLink}
//                         target="_blank"
//                         rel="noopener noreferrer"
//                         className="btn btn-outline-success btn-sm"
//                       >
//                         Pay Now
//                       </a>
//                       &nbsp;
//                       {inv.invoicepdfURL ? (
//                         <a
//                           href={inv.invoicepdfURL}
//                           target="_blank"
//                           rel="noopener noreferrer"
//                           className="btn btn-primary btn-sm"
//                           download
//                         >
//                           Download {/* <FaDownload /> */}
//                         </a>
//                       ) : (
//                         <span className="text-muted">-</span>
//                       )}
//                     </td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td colSpan="9" className="text-center">
//                     No invoices found
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </CommonContainer>
//     </>
//   );
// };

// export default InvoiceReportComponent;
